import { useNavigation } from '@react-navigation/native';
import { useResponsiveHeight } from '@/components/Util/reactResponsiveDimensions';
import React from 'react';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useDialog } from '@/hooks/useDialog';
import { Text } from '@/components/Text';
import {
  confirmPasswordValidate,
  passwordValidate,
} from '@/utils/passwordUtils';
import { PREPARED_DIALOGS } from '@/components/ui/shared/Dialog/constants';

import { SharedFormField } from '@/components/SharedComponents/FormField/SharedFormField';
import { styles } from './styles';
import Card from '@/components/ui/therapist/Card';
import { Button } from '@/components/ui/therapist/Button';
import { usePasswordFormController } from '@/components/ChangePassword/usePasswordFormController';

type TherapistPasswordChangeFormProps = {
  passwordResetToken?: string;
};

function TherapistPasswordChangeForm({
  passwordResetToken,
}: TherapistPasswordChangeFormProps) {
  const { showDialog, removeDialog } = useDialog();
  const navigation = useNavigation();

  const localDialogOptions = {
    success: {
      title: 'Passwort geändert',
      content: 'Passwort wurde erfolgreich geändert.',
      confirmAction: {
        label: 'weiter',
        onPress: () => {
          removeDialog();
          navigation.navigate('Home');
        },
      },
    },
    error: {
      title: 'Login fehlgeschlagen!',
      content:
        'Ihr Passwort oder Ihre E-Mail-Adresse sind falsch. Oder Sie haben sich noch nicht bei neolexon registriert!',
      danger: true,
    },
  };

  const { control, newPasswordFilled, handlePasswordSubmit } =
    usePasswordFormController({
      passwordResetToken,
      onSuccess: () => showDialog(localDialogOptions.success),
      onError: () => showDialog(localDialogOptions.error),
      onNetworkError: () => showDialog(PREPARED_DIALOGS.noInternetDialog()),
      onResetSuccess: () =>
        navigation.navigate('PublicConfirmationPage', {
          title: 'Ihr Passwort wurde erfolgreich zurückgesetzt',
          text: 'Sie können dieses Fenster schließen und sich erneut in der Neolexon–App anmelden.',
        }),
      onResetError: () =>
        showDialog({
          title: 'Fehler beim Zurücksetzen',
          content:
            'Leider konnte Ihr Passwort nicht zurückgesetzt werden. Versuchen Sie es bitte erneut.',
          danger: true,
        }),
    });

  return (
    <View style={styles.formScreen}>
      <KeyboardAwareScrollView
        contentContainerStyle={[styles.contentWrapper, { flexGrow: 1 }]}
        enableOnAndroid={true}
        extraHeight={useResponsiveHeight(30)}
        extraScrollHeight={150}
      >
        <View style={{ marginTop: 64 }} />
        <View style={styles.cardContainer}>
          <Card>
            <Text style={styles.infoText}>
              Geben Sie Ihr aktuelles Passwort ein und wählen Sie ein neues,
              starkes Passwort. Für maximale Sicherheit empfehlen wir, ein
              zufällig generiertes Passwort zu verwenden und es in einem
              Passwortmanager zu speichern.
            </Text>
          </Card>
        </View>
        <View style={styles.cardContainer}>
          <Card>
            <View style={styles.formFieldContainer}>
              {passwordResetToken ? null : (
                <SharedFormField
                  control={control}
                  name="currentPassword"
                  label="Aktuelles Passwort"
                  rules={{ required: 'Pflichtfeld' }}
                  secureTextEntry
                  usePasswordInput
                />
              )}
            </View>
          </Card>
        </View>
        <View style={styles.cardContainer}>
          <Card>
            <View style={styles.formFieldContainer}>
              <SharedFormField
                control={control}
                name="newPassword"
                label="Neues Passwort"
                additionalInfo={
                  'Ihr neues Passwort sollte mindestens 8 Zeichen lang sein und idealerweise Groß- und Kleinbuchstaben, Zahlen oder Sonderzeichen enthalten.'
                }
                rules={{
                  required: 'Pflichtfeld',
                  minLength: {
                    value: 8,
                    message:
                      'Passwort muss mindestens 8 Zeichen lang sein und darf Buchstaben, Zahlen, Sonderzeichen und Leerzeichen enthalten.',
                  },
                  validate: (value: string) => passwordValidate(value),
                }}
                secureTextEntry
                usePasswordInput
              />
            </View>
            <View style={styles.sectionDivider} />
            <View style={styles.formFieldContainer}>
              <SharedFormField
                control={control}
                name="verifyPassword"
                label="Passwort wiederholen"
                rules={{
                  required: 'Pflichtfeld',
                  validate: (value: string) =>
                    confirmPasswordValidate(value, newPasswordFilled),
                }}
                secureTextEntry
                usePasswordInput
              />
            </View>
          </Card>
        </View>
        <View style={[{ width: '100%', maxWidth: 900, paddingBottom: 16 }]}>
          <View style={styles.actionRow}>
            <Button
              label="Abbrechen"
              look="tertiary"
              onPress={() => {
                navigation.goBack();
              }}
            />
            <Button label="Passwort speichern" onPress={handlePasswordSubmit} />
          </View>
        </View>
      </KeyboardAwareScrollView>
    </View>
  );
}

export default TherapistPasswordChangeForm;
